import classNames from "classnames"
import { useState, useEffect, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import Close from "~/icons/Close"
import { NotificationContext } from "~/hooks/useNotifications"
import type {
  Notification,
  NotificationContent
} from "~/hooks/useNotifications"

const NotificationItem = ({
  notification,
  onDismiss
}: {
  notification: Notification
  onDismiss: () => void
}) => {
  const type = {
    success: "success",
    error: "danger"
  }[notification.type]

  return createPortal(
    <div className="fixed right-6 top-6 z-60 ml-6 space-y-6">
      <div
        role="alert"
        className={classNames(
          "flex items-center space-x-3 rounded-md p-4 text-white",
          {
            "bg-camp-burnt-orange": type == "danger",
            "bg-light-green": type == "success"
          }
        )}
      >
        <span className="leading-7">{notification.content}</span>

        <button onClick={onDismiss} type="button" title="Close">
          <div className="w-3">
            <Close />
          </div>
        </button>
      </div>
    </div>,
    document.body
  )
}

export const NotificationsProvider = ({ children }: { children: any }) => {
  const [notification, setNotification] = useState<Notification | null>(null)

  const showNotification = useCallback((notification: Notification) => {
    setNotification(notification)
  }, [])

  const showSuccess = useCallback((content: NotificationContent) => {
    setNotification({ type: "success", content, autohide: true })
  }, [])

  const showError = useCallback((content: NotificationContent) => {
    setNotification({ type: "error", content, autohide: false })
  }, [])

  const dismissNotification = useCallback(() => {
    setNotification(null)
  }, [])

  const value = useMemo(
    () => ({ showNotification, showSuccess, showError }),
    [showNotification, showSuccess, showError]
  )

  // Auto-hide.
  useEffect(() => {
    if (!!notification && notification.autohide) {
      setTimeout(() => {
        dismissNotification()
      }, 5000)
    }
  }, [notification, dismissNotification])

  return (
    <NotificationContext.Provider value={value}>
      {notification && (
        <NotificationItem
          notification={notification}
          onDismiss={dismissNotification}
        />
      )}

      {children}
    </NotificationContext.Provider>
  )
}
